import React, { useEffect, useMemo, useState } from "react";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css";
import { Col, Form, Row } from "react-bootstrap";
import { FaEye } from "react-icons/fa";
import { FaCircleXmark } from "react-icons/fa6";
import { useDispatch, useSelector } from "react-redux";
import {
  AddButton,
  Button,
  Input,
  SubTitle,
} from "../../Components/CustomComponents";
import ModalContainer from "../../Components/ModalContainer";
import Table from "../../Components/Table/Table";
import { fetchAllUsers } from "../../Data/features/userSlice";
import Toast from "../../Hooks/useToast";
import useAppointmentController from "../../controllers/AppointmentControllers/useAppointmentController";
import { getAgentService, getService } from "../../redux/services";
import { api_urls } from "../../utilities";
import modalStyles from "../../Components/Modals/styles/editModal.module.scss";
import styles from "./styles/appointment_management.module.scss";

function getUserDisplayName(row) {
  return (
    row.name ||
    row.username ||
    `${row.firstName || ""} ${row.lastName || ""}`.trim() ||
    ""
  );
}

function isArtistUser(user) {
  const raw = user?.user_type ?? user?.role ?? user?.type;
  if (raw == null || raw === "") return false;
  return String(raw).trim().toLowerCase() === "artist";
}

function formatTimeShort(t) {
  if (!t || typeof t !== "string") return "";
  return t.length >= 5 ? t.slice(0, 5) : t;
}

function getArtistName(row) {
  return row.Artist?.name ?? row.artist?.name ?? "—";
}

function getUserName(row) {
  return row.User?.name ?? row.user?.name ?? "—";
}

function getLocationName(row) {
  return row.Location?.name ?? row.location?.name ?? "—";
}

function getTimeDisplay(row) {
  const date = row.date ?? "—";
  const from = formatTimeShort(row.time_from);
  const to = formatTimeShort(row.time_to);
  if (!row.time_from && !row.time_to) return String(date);
  return `${date} · ${from} – ${to}`;
}

function formatTypeOfService(row) {
  const arr = row.type_of_service ?? row.typeOfService;
  if (!Array.isArray(arr) || !arr.length) return "—";
  const titles = arr.map((s) => s?.title ?? s?.name).filter(Boolean);
  return titles.length ? titles.join(", ") : "—";
}

function formatBool(v) {
  if (v === true || v === false) return v ? "Yes" : "No";
  if (v == null) return "—";
  return String(v);
}

function normalizeQuoteList(inner) {
  if (inner == null) return [];
  if (Array.isArray(inner)) return inner;
  return (
    inner.data ??
    inner.quote_requests ??
    inner.quoteRequests ??
    inner.quotes ??
    inner.records ??
    inner.list ??
    []
  );
}

function getQuoteServiceId(quote) {
  const fromFormArray = quote?.form_data?.type_of_service;
  if (Array.isArray(fromFormArray) && fromFormArray.length) {
    const picked = fromFormArray[0]?.id ?? fromFormArray[0]?.service_id;
    if (picked != null && picked !== "") return picked;
  }
  const fromTopLevel = quote?.type_of_service;
  if (Array.isArray(fromTopLevel) && fromTopLevel.length) {
    const picked = fromTopLevel[0]?.id ?? fromTopLevel[0]?.service_id;
    if (picked != null && picked !== "") return picked;
  }
  return "";
}

function getQuoteServiceLabel(quote) {
  const fromFormArray = quote?.form_data?.type_of_service;
  if (Array.isArray(fromFormArray) && fromFormArray.length) {
    return fromFormArray
      .map((item) => item?.title ?? item?.name ?? item?.id)
      .filter(Boolean)
      .join(", ");
  }
  const fromTopLevel = quote?.type_of_service;
  if (Array.isArray(fromTopLevel) && fromTopLevel.length) {
    return fromTopLevel
      .map((item) => item?.title ?? item?.name ?? item?.id)
      .filter(Boolean)
      .join(", ");
  }
  return "-";
}

const DetailLine = ({ label, value }) => (
  <div className={styles.detailLine}>
    <span className={styles.detailLabel}>{label}</span>
    <span className={styles.detailValue}>{value ?? "—"}</span>
  </div>
);

const AppointmentManagement = () => {
  const dispatch = useDispatch();
  const portal = useSelector((state) => state.auth?.portal);
  const allUsers = useSelector((state) => state.user.artists);
  const usersLoading = useSelector((state) => state.user.artistsLoading);

  const {
    values: { list: rowData, loading, creating, statusUpdating },
    functions: { handleCreateAppointment, handleSetAppointmentStatus },
  } = useAppointmentController();

  const [indicationMessage, setIndicationMessage] = useState("loading...");
  const [createModal, setCreateModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [selectedArtistId, setSelectedArtistId] = useState("");
  const [appointmentDate, setAppointmentDate] = useState("");
  const [timeFrom, setTimeFrom] = useState("");
  const [timeTo, setTimeTo] = useState("");
  const [selectedQuoteId, setSelectedQuoteId] = useState("");
  const [quoteOptions, setQuoteOptions] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);

  const artistUsers = useMemo(
    () => (Array.isArray(allUsers) ? allUsers.filter(isArtistUser) : []),
    [allUsers]
  );
  console.log("[AppointmentManagement] artistUsers:", artistUsers);

  useEffect(() => {
    if (!createModal) return;
    dispatch(fetchAllUsers());
  }, [createModal, dispatch]);

  useEffect(() => {
    if (!createModal) return;
    const fetchQuotes = async () => {
      setQuotesLoading(true);
      try {
        const response =
          portal === "agent"
            ? await getAgentService(api_urls.agentQuotes, {})
            : await getService(api_urls.quoteRequests, {});
        const envelope = response?.data ?? {};
        const inner = envelope?.data ?? envelope;
        const list = normalizeQuoteList(inner);
        setQuoteOptions(Array.isArray(list) ? list : []);
      } catch (error) {
        const msg =
          error?.data?.message ?? error?.message ?? "Failed to load quotes";
        Toast(msg, "error");
        setQuoteOptions([]);
      } finally {
        setQuotesLoading(false);
      }
    };
    fetchQuotes();
  }, [createModal, portal]);

  useEffect(() => {
    if (loading) {
      setIndicationMessage("loading...");
      return;
    }
    setIndicationMessage(rowData.length ? "" : "No appointments found");
  }, [loading, rowData.length]);

  const handleViewOpen = (row) => {
    setSelectedRow(row);
    setViewModal(true);
  };

  const handleViewClose = () => {
    setViewModal(false);
    setSelectedRow(null);
  };

  const handleCreateModalOpen = () => setCreateModal(true);

  const handleCreateModalClose = () => {
    setCreateModal(false);
    setSelectedArtistId("");
    setAppointmentDate("");
    setTimeFrom("");
    setTimeTo("");
    setSelectedQuoteId("");
    setQuoteOptions([]);
    setQuotesLoading(false);
  };

  const handleSubmitCreate = async () => {
    const selectedQuote = quoteOptions.find(
      (q) => String(q.id) === String(selectedQuoteId)
    );
    const selectedServiceId = getQuoteServiceId(selectedQuote);

    if (
      !selectedArtistId ||
      !selectedQuoteId ||
      !appointmentDate ||
      !timeFrom ||
      !timeTo ||
      !selectedServiceId
    ) {
      Toast("Please fill all appointment fields and select a valid quote", "error");
      return;
    }

    const artist = artistUsers.find(
      (u) => String(u.id) === String(selectedArtistId)
    );
    console.log("[handleSubmitCreate] artist:", artist);
    const padTime = (t) => (t && t.length === 5 ? `${t}:00` : t);

    try {
      await handleCreateAppointment({
        date: appointmentDate,
        time_from: padTime(timeFrom),
        time_to: padTime(timeTo),
        artist_id: artist?.id ?? selectedArtistId,
        quote_request_id: selectedQuote?.id ?? selectedQuoteId,
        type_of_service: [selectedServiceId],
      });
      handleCreateModalClose();
    } catch {
      /* Toast in controller */
    }
  };

  const tableRows = useMemo(
    () =>
      rowData.map((row) => ({
        ...row,
        artist_display: getArtistName(row),
        user_display: getUserName(row),
        location_display: getLocationName(row),
        time_display: getTimeDisplay(row),
      })),
    [rowData]
  );

  const columns = [
    {
      dataField: "id",
      text: "Appointment ID",
      sort: true,
      headerStyle: { width: "130px" },
      formatter: (cell) => (cell != null ? String(cell) : "—"),
    },
    {
      dataField: "artist_display",
      text: "Artist",
      sort: true,
      headerStyle: { width: "200px" },
    },
    {
      dataField: "user_display",
      text: "User",
      sort: true,
      headerStyle: { width: "200px" },
    },
    {
      dataField: "location_display",
      text: "Location",
      sort: true,
      headerStyle: { width: "180px" },
    },
    {
      dataField: "time_display",
      text: "Date & time",
      sort: true,
      headerStyle: { width: "280px" },
    },
    {
      dataField: "status",
      text: "Status",
      sort: true,
      headerAlign: "center",
      align: "center",
      headerStyle: { width: "140px" },
      formatter: (cell) => (
        <span
          className={`${styles.statusBadge} ${
            cell === "completed"
              ? styles.completed
              : cell === "cancelled"
              ? styles.cancelled
              : styles.pending
          }`}
        >
          {(cell || "pending").toUpperCase()}
        </span>
      ),
    },
    {
      dataField: "actions",
      text: "Actions",
      headerAlign: "center",
      align: "center",
      headerStyle: { width: "120px" },
      formatter: (_cell, row) => (
        <div className="d-flex justify-content-center align-items-center">
          <button
            type="button"
            className={styles.viewBtn}
            onClick={() => handleViewOpen(row)}
            aria-label="View appointment details"
          >
            <FaEye />
            <span>View</span>
          </button>
        </div>
      ),
    },
  ];

  const indication = () => indicationMessage;

  const d = selectedRow;

  const statusNorm = (d?.status || "").toLowerCase();
  const isTerminal =
    statusNorm === "completed" || statusNorm === "cancelled";

  return (
    <div className={styles.MainContainer}>
      {createModal ? (
        <ModalContainer handleClose={handleCreateModalClose}>
          <div className={modalStyles.modalContainer}>
            <div className="d-flex align-items-center w-100 justify-content-between mb-4">
              <p className="fs-4 fw-medium m-0">Create Appointment</p>
              <span className="bg-dark bg-opacity-75 d-flex p-3 rounded-5">
                <FaCircleXmark
                  role="button"
                  onClick={handleCreateModalClose}
                  style={{ cursor: "pointer" }}
                />
              </span>
            </div>

            <div className="w-100">
              <Row>
                <Col md={12}>
                  <p className="m-0">Artist</p>
                  <Form.Select
                    className={`${styles.selectField} mt-2 mb-3`}
                    value={selectedArtistId}
                    onChange={(e) => setSelectedArtistId(e.target.value)}
                    disabled={usersLoading}
                  >
                    <option value="">
                      {usersLoading
                        ? "Loading artists..."
                        : artistUsers.length === 0
                        ? "No artists found"
                        : "Select artist"}
                    </option>
                    {artistUsers.map((user) => (
                      <option key={user.id} value={String(user.id)}>
                        {getUserDisplayName(user) || `User #${user.id}`}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <p className="m-0">Date</p>
                  <Input
                    classes="mt-2 mb-3"
                    type="date"
                    value={appointmentDate}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setAppointmentDate(e.target.value)}
                  />
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <p className="m-0">Time From</p>
                  <Input
                    classes="mt-2 mb-3"
                    type="time"
                    value={timeFrom}
                    onChange={(e) => setTimeFrom(e.target.value)}
                  />
                </Col>
                <Col md={6}>
                  <p className="m-0">Time To</p>
                  <Input
                    classes="mt-2 mb-3"
                    type="time"
                    value={timeTo}
                    onChange={(e) => setTimeTo(e.target.value)}
                  />
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <p className="m-0">Quote</p>
                  <Form.Select
                    className={`${styles.selectField} mt-2 mb-3`}
                    value={selectedQuoteId}
                    onChange={(e) => setSelectedQuoteId(e.target.value)}
                    disabled={quotesLoading}
                  >
                    <option value="">
                      {quotesLoading
                        ? "Loading quotes..."
                        : quoteOptions.length === 0
                        ? "No quotes found"
                        : "Select quote ID"}
                    </option>
                    {quoteOptions.map((quote) => (
                      <option key={quote.id} value={String(quote.id)}>
                        {`Quote #${quote.id}`}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <p className="m-0">Type of Service</p>
                  <Input
                    classes={`${styles.selectField} mt-2 mb-3`}
                    type="text"
                    value={
                      selectedQuoteId
                        ? getQuoteServiceLabel(
                            quoteOptions.find(
                              (q) => String(q.id) === String(selectedQuoteId)
                            )
                          )
                        : "Select quote first"
                    }
                    disable
                  />
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Button
                    classes="mt-4"
                    text={creating ? "Saving..." : "Create Appointment"}
                    onClick={handleSubmitCreate}
                    disabled={creating}
                  />
                </Col>
              </Row>
            </div>
          </div>
        </ModalContainer>
      ) : null}

      {viewModal && d ? (
        <ModalContainer handleClose={handleViewClose}>
          <div className={modalStyles.modalContainer}>
            <div className="d-flex align-items-center w-100 justify-content-between mb-4">
              <p className="fs-4 fw-medium m-0">Appointment details</p>
              <span className="bg-dark bg-opacity-75 d-flex p-3 rounded-5">
                <FaCircleXmark
                  role="button"
                  onClick={handleViewClose}
                  style={{ cursor: "pointer" }}
                />
              </span>
            </div>

            <div className={styles.detailScroll}>
              <Row className="g-3">
                <Col md={12}>
                  <DetailLine
                    label="Appointment ID"
                    value={d.id != null ? String(d.id) : "—"}
                  />
                </Col>
                <Col md={6}>
                  <DetailLine label="Artist" value={getArtistName(d)} />
                </Col>
                <Col md={6}>
                  <DetailLine label="User" value={getUserName(d)} />
                </Col>
                <Col md={6}>
                  <DetailLine label="Location" value={getLocationName(d)} />
                </Col>
                <Col md={6}>
                  <DetailLine
                    label="Date & time"
                    value={getTimeDisplay(d)}
                  />
                </Col>
                <Col md={6}>
                  <DetailLine label="Status" value={d.status ?? "—"} />
                </Col>
                <Col md={6}>
                  <DetailLine
                    label="Type of service"
                    value={formatTypeOfService(d)}
                  />
                </Col>
                <Col md={12}>
                  <DetailLine label="Notes" value={d.notes ?? "—"} />
                </Col>
                <Col md={6}>
                  <DetailLine
                    label="Under 18"
                    value={formatBool(d.under_18)}
                  />
                </Col>
                <Col md={6}>
                  <DetailLine
                    label="Covering an old tattoo"
                    value={formatBool(d.covering_an_old)}
                  />
                </Col>
                <Col md={6}>
                  <DetailLine
                    label="Existing tattoo"
                    value={d.existing_tattoo ?? "—"}
                  />
                </Col>
                <Col md={6}>
                  <DetailLine label="Color" value={d.color ?? "—"} />
                </Col>
                <Col md={6}>
                  <DetailLine
                    label="Tattoo location"
                    value={d.tattoos_be_located ?? "—"}
                  />
                </Col>
                <Col md={6}>
                  <DetailLine label="Size" value={d.how_big ?? "—"} />
                </Col>
                <Col md={6}>
                  <DetailLine
                    label="Payment type"
                    value={d.payment_type ?? "—"}
                  />
                </Col>
                <Col md={6}>
                  <DetailLine
                    label="Payment amount"
                    value={
                      d.payment_amount != null && d.payment_amount !== ""
                        ? String(d.payment_amount)
                        : "—"
                    }
                  />
                </Col>
                <Col md={12}>
                  <DetailLine
                    label="Cancellation reason"
                    value={d.cancellation_reason ?? "—"}
                  />
                </Col>
              </Row>

              <div className={`d-flex flex-wrap gap-2 mt-4 pt-3 ${styles.modalActions}`}>
                <button
                  type="button"
                  className={`${styles.actionBtn} ${styles.completeBtn}`}
                  disabled={statusUpdating || isTerminal}
                  onClick={async () => {
                    try {
                      await handleSetAppointmentStatus(d.id, "completed");
                      handleViewClose();
                    } catch {
                      /* Toast in controller */
                    }
                  }}
                >
                  Mark completed
                </button>
                <button
                  type="button"
                  className={`${styles.actionBtn} ${styles.cancelBtn}`}
                  disabled={statusUpdating || isTerminal}
                  onClick={async () => {
                    try {
                      await handleSetAppointmentStatus(d.id, "cancelled");
                      handleViewClose();
                    } catch {
                      /* Toast in controller */
                    }
                  }}
                >
                  Mark cancelled
                </button>
              </div>
            </div>
          </div>
        </ModalContainer>
      ) : null}

      <div className={styles.headerContainer}>
        <div className="w-100 flex-between">
          <SubTitle text={"Appointment Management"} />
          <AddButton
            text="Create Appointment"
            onClick={handleCreateModalOpen}
          />
        </div>
      </div>

      <div className={styles.mainTable}>
        <Table data={tableRows} columns={columns} indication={indication} />
      </div>
    </div>
  );
};

export default AppointmentManagement;
